import React from "react";
import { Box, Button, Paper, Skeleton, Typography } from "@mui/material";

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const Dato = ({ etiqueta, sx, children }) => (
  <Box component="div" sx={{ minWidth: 0, ...sx }}>
    <Typography
      component="dt"
      variant="caption"
      sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
    >
      {etiqueta}
    </Typography>
    <Box component="dd" sx={{ m: 0, mt: 0.5 }}>
      {children}
    </Box>
  </Box>
);

/**
 * Resumen del cliente bajo el encabezado, con lo que ya se cargó (sin pedir nada nuevo).
 *
 * @param {object} props
 * @param {{identification: string, phone: string, address: string, ubicacion?: string}|null} props.cliente
 *   Datos del cliente; null mientras no han llegado.
 * @param {boolean} [props.fallo] La pestaña "Cliente" no pudo cargar: se deja de esperar y se muestra "—".
 * @param {number|null} [props.creditos] Cantidad de créditos; solo se conoce cuando se abrió esa pestaña.
 * @param {Function} [props.onVerCreditos] Lleva a la pestaña de créditos, donde se carga la lista.
 */
const ResumenCliente = ({ cliente, creditos = null, fallo = false, onVerCreditos }) => {
  const cargando = !cliente && !fallo;

  const cargandoDato = (ancho) => (
    <Skeleton variant="text" width={ancho} sx={{ fontSize: "1.25rem", ...sinAnimacion }} />
  );

  const valor = (texto, { sinCorte = false } = {}) =>
    cargando ? (
      cargandoDato(110)
    ) : (
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: sinCorte ? "nowrap" : "normal",
          overflowWrap: "anywhere",
        }}
      >
        {texto || "—"}
      </Typography>
    );

  // La cantidad de créditos llega al abrir la pestaña "Créditos": hasta entonces se invita a abrirla.
  let contenidoCreditos;
  if (cargando) {
    contenidoCreditos = cargandoDato(40);
  } else if (creditos === null) {
    contenidoCreditos = (
      <>
        <Typography component="span" sx={{ fontWeight: 700 }}>
          —
        </Typography>
        {onVerCreditos && (
          <Button
            size="small"
            variant="text"
            onClick={onVerCreditos}
            sx={{ display: "flex", px: 0, minWidth: 0 }}
          >
            Ver los créditos
          </Button>
        )}
      </>
    );
  } else {
    contenidoCreditos = (
      <Typography component="span" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {creditos}
      </Typography>
    );
  }

  return (
    <Paper
      component="section"
      aria-label="Resumen del cliente"
      aria-busy={cargando || undefined}
      sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}
    >
      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        <Dato etiqueta="Cédula">{valor(cliente?.identification, { sinCorte: true })}</Dato>
        <Dato etiqueta="Teléfono">{valor(cliente?.phone, { sinCorte: true })}</Dato>
        <Dato etiqueta="Créditos">{contenidoCreditos}</Dato>
        <Dato etiqueta="Dirección" sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
          {valor(cliente?.address)}
          {!cargando && cliente?.ubicacion ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", overflowWrap: "anywhere" }}
            >
              {cliente.ubicacion}
            </Typography>
          ) : null}
        </Dato>
      </Box>
    </Paper>
  );
};

export default ResumenCliente;
